import { ReactNode, useEffect, useId, useMemo, useState } from "react";
import { useModalContext } from "../components/ModalProvider";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { userAPI } from "../UserAPI";
import dayjs from "dayjs";

import type { Column, ColumnDef } from "@tanstack/react-table";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronsUpDown,
    CircleStop,
    Clock,
    HelpCircle,
    Pause,
    Play,
    XCircle,
    CheckCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUrlPageState } from "../utilities/dataTablePagination";
import { JobStatus } from "@the-real-insight/tri-model";

interface Properties {}

interface StatusOption {
  key: string;
  text: string;
}

interface JobDetailViewProps {
  job: any | null;
  onDismiss: () => void;
}

/**
 * Statuszeichen eines Jobs. Ersetzt die Hex-Farben des Bestands durch Tokens;
 * die Zuordnung Status → Symbol bleibt unverändert.
 */
const getStatusIcon = (status: string) => {
  const statusLower = (status || '').toLowerCase();

  switch (statusLower) {
    case 'running':
    case 'active':
    case 'in_progress':
      return { Icon: Play, className: 'text-primary', tooltip: status };
    case 'completed':
    case 'success':
    case 'done':
    case 'finished':
      return { Icon: CheckCircle2, className: 'text-success', tooltip: status };
    case 'failed':
    case 'error':
    case 'failure':
      return { Icon: XCircle, className: 'text-destructive', tooltip: status };
    case 'pending':
    case 'waiting':
    case 'queued':
      return { Icon: Clock, className: 'text-warning', tooltip: status };
    case 'paused':
    case 'suspended':
      return { Icon: Pause, className: 'text-warning', tooltip: status };
    case 'cancelled':
    case 'canceled':
      return { Icon: CircleStop, className: 'text-muted-foreground', tooltip: status };
    default:
      return { Icon: HelpCircle, className: 'text-muted-foreground', tooltip: status || '-' };
  }
};

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/** Leerzustand: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
      {action}
    </div>
  );
}

/**
 * Mehrfachauswahl der Job-Status — ersetzt das `multiSelect`-Dropdown. Leere
 * Auswahl heißt weiterhin „kein Statusfilter“, nicht „nichts anzeigen“.
 */
function StatusMultiSelect({
  label,
  placeholder,
  options,
  selectedKeys,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: StatusOption[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const { t } = useTranslation();

  const toggleOption = (key: string) => {
    onChange(
      selectedKeys.includes(key)
        ? selectedKeys.filter((candidate) => candidate !== key)
        : [...selectedKeys, key]
    );
  };

  const triggerText =
    selectedKeys.length === 0
      ? placeholder
      : options
          .filter((option) => selectedKeys.includes(option.key))
          .map((option) => option.text)
          .join(", ");

  return (
    <div className="flex w-[300px] min-w-0 max-w-full flex-col gap-1.5">
      <Label id={labelId}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-labelledby={labelId}
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className={selectedKeys.length === 0 ? "truncate text-muted-foreground" : "truncate"}>
              {triggerText}
            </span>
            <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandList>
              <CommandEmpty>{t('settingsPanel.settingsPanel.jobPanel.noStatuses')}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.key}
                    value={option.text}
                    className="gap-2"
                    onSelect={() => toggleOption(option.key)}
                  >
                    <Checkbox
                      checked={selectedKeys.includes(option.key)}
                      tabIndex={-1}
                      className="pointer-events-none"
                    />
                    <span className="min-w-0 break-words">{option.text}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function JobDetailView({ job, onDismiss: _onDismiss }: JobDetailViewProps) {
  const { t } = useTranslation();

  if (!job) {
    return null;
  }

  // Helper function to convert JSON object to table format
  const renderJsonAsTable = (obj: any, title: string) => {
    if (!obj || typeof obj !== 'object') {
      return (
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">
            {obj === null || obj === undefined ? '-' : String(obj)}
          </p>
        </div>
      );
    }

    const entries = Object.entries(obj);
    if (entries.length === 0) {
      return (
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">-</p>
        </div>
      );
    }

    return (
      <div className="min-w-0">
        <h3 className="mb-2 text-sm font-semibold">{title}</h3>
        <div className="min-w-0 overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold text-foreground">
                  {t('settingsPanel.settingsPanel.jobPanel.key')}
                </TableHead>
                <TableHead className="font-bold text-foreground">
                  {t('settingsPanel.settingsPanel.jobPanel.value')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(([key, value], index) => (
                <TableRow key={index}>
                  <TableCell className="min-w-0 break-words align-top font-bold">{key}</TableCell>
                  <TableCell className="min-w-0 align-top">
                    {(() => {
                      if (value === null || value === undefined) {
                        return <span className="text-muted-foreground">-</span>;
                      }
                      if (typeof value === 'object') {
                        return (
                          <pre className="m-0 whitespace-pre-wrap break-words font-mono text-xs">
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        );
                      }
                      return <span className="min-w-0 break-words">{String(value)}</span>;
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  const statusIcon = getStatusIcon(job.status);
  const StatusIconComponent = statusIcon.Icon;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center">
                <StatusIconComponent aria-hidden className={`h-6 w-6 ${statusIcon.className}`} />
                <span className="sr-only">{statusIcon.tooltip}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>{statusIcon.tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex min-w-0 flex-wrap gap-4">
        <div className="min-w-0 flex-1 basis-[300px]">
          {renderJsonAsTable(job.parameter, t('settingsPanel.settingsPanel.jobPanel.parameter'))}
        </div>
        <div className="min-w-0 flex-1 basis-[300px]">
          {renderJsonAsTable(job.stat, t('settingsPanel.settingsPanel.jobPanel.stat'))}
        </div>
      </div>
    </div>
  );
}

export default function JobPanel(_properties: Properties) {
  const { t } = useTranslation();
  const jobListPagination = useUrlPageState('jobListPage');
  const fieldId = useId();
  const { pushError } = useModalContext() as any;
  const [_authentication] = useAtom(authenticationAtom) as any;
  const [jobs, setJobs] = useState<any[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<any[]>([]);
  const [selectedJobStatuses, setSelectedJobStatuses] = useState<string[]>(['running']);
  const [typeFilterText, setTypeFilterText] = useState<string>('');
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [jobDetailDialogOpen, setJobDetailDialogOpen] = useState(false);

  const typeFilterId = `${fieldId}-type-filter`;

  // Get all available JobStatus values
  const jobStatusOptions: StatusOption[] = useMemo(() => {
    try {
      // Try to get JobStatus enum values
      if (JobStatus && typeof JobStatus === 'object') {
        const statuses = Object.values(JobStatus).filter((s: any) => typeof s === 'string');
        return statuses.map((status: any) => ({
          key: status,
          text: status,
        }));
      }
      // Fallback: extract unique statuses from jobs
      const uniqueStatuses = Array.from(new Set(jobs.map((job: any) => job.status).filter(Boolean)));
      return uniqueStatuses.map((status: string) => ({
        key: status,
        text: status,
      }));
    } catch (error) {
      // Fallback: extract unique statuses from jobs
      const uniqueStatuses = Array.from(new Set(jobs.map((job: any) => job.status).filter(Boolean)));
      return uniqueStatuses.map((status: string) => ({
        key: status,
        text: status,
      }));
    }
  }, [jobs]);


  const loadJobs = async () => {
    setLoading(true);
    try {
      const jobsData = await userAPI.getJobs();
      // Convert plain objects to Job instances if needed
      const jobsList = Array.isArray(jobsData) ? jobsData : [];
      setJobs(jobsList);
    } catch (error: any) {
      console.error("Failed to fetch jobs:", error);
      pushError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  // Filter jobs based on selected statuses and type text filter
  useEffect(() => {
    let filtered = jobs;

    // Filter by status
    if (selectedJobStatuses.length > 0) {
      filtered = filtered.filter((job: any) =>
        selectedJobStatuses.includes(job.status)
      );
    }

    // Filter by type text (case-insensitive)
    if (typeFilterText.trim().length > 0) {
      const filterLower = typeFilterText.toLowerCase();
      filtered = filtered.filter((job: any) =>
        job.type && job.type.toLowerCase().includes(filterLower)
      );
    }

    setFilteredJobs(filtered);
  }, [jobs, selectedJobStatuses, typeFilterText]);

  // Der abgelöste Tabellen-Bestand startete mit `defaultSortFieldId="createdAt"`
  // absteigend; die Grundordnung wird hier vorgegeben, die Spaltenköpfe sortieren
  // darüber hinaus wie gehabt.
  const sortedJobs = useMemo(
    () =>
      [...filteredJobs].sort(
        (a: any, b: any) =>
          (b.createdAt ? new Date(b.createdAt).getTime() : 0) -
          (a.createdAt ? new Date(a.createdAt).getTime() : 0)
      ),
    [filteredJobs]
  );

  const handleJobClick = (job: any) => {
    setSelectedJob(job);
    setJobDetailDialogOpen(true);
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'name',
      size: 250,
      accessorFn: (job: any) => job.type || '',
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t('settingsPanel.settingsPanel.jobPanel.name')}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">{row.original.type || '-'}</div>
      ),
    },
    {
      id: 'status',
      size: 100,
      accessorFn: (job: any) => job.status || '',
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t('settingsPanel.settingsPanel.jobPanel.status')}
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const statusIcon = getStatusIcon(row.original.status);
        const IconComponent = statusIcon.Icon;

        return (
          <div className="flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center">
                    <IconComponent aria-hidden className={`h-5 w-5 ${statusIcon.className}`} />
                    <span className="sr-only">{statusIcon.tooltip}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{statusIcon.tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    {
      id: 'createdAt',
      size: 200,
      accessorFn: (job: any) => (job.createdAt ? new Date(job.createdAt).getTime() : 0),
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t('settingsPanel.settingsPanel.jobPanel.startTime')}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.createdAt
            ? `${dayjs(row.original.createdAt).format(t('global.dateShort'))} ${dayjs(row.original.createdAt).format('HH:mm')}`
            : '-'}
        </div>
      ),
    },
    {
      id: 'updatedAt',
      size: 200,
      accessorFn: (job: any) => (job.updatedAt ? new Date(job.updatedAt).getTime() : 0),
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t('settingsPanel.settingsPanel.jobPanel.lastUpdate')}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.updatedAt
            ? `${dayjs(row.original.updatedAt).format(t('global.dateShort'))} ${dayjs(row.original.updatedAt).format('HH:mm')}`
            : '-'}
        </div>
      ),
    },
  ];

  const filterActive = selectedJobStatuses.length > 0 || typeFilterText.trim().length > 0;

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <StatusMultiSelect
          label={t('settingsPanel.settingsPanel.jobPanel.statusFilter')}
          placeholder={t('settingsPanel.settingsPanel.jobPanel.selectStatuses')}
          options={jobStatusOptions}
          selectedKeys={selectedJobStatuses}
          onChange={setSelectedJobStatuses}
        />
        <div className="flex w-[300px] min-w-0 max-w-full flex-col gap-1.5">
          <Label htmlFor={typeFilterId}>
            {t('settingsPanel.settingsPanel.jobPanel.typeFilter')}
          </Label>
          <Input
            id={typeFilterId}
            placeholder={t('settingsPanel.settingsPanel.jobPanel.filterByType')}
            value={typeFilterText}
            onChange={(event) => setTypeFilterText(event.target.value || '')}
          />
        </div>
      </div>
      <div className="min-w-0 overflow-x-auto">
        <DataTable
          columns={columns}
          data={sortedJobs}
          loading={loading}
          pageSize={10}
          page={jobListPagination.page}
          onPageChange={jobListPagination.onPageChange}
          onRowClick={handleJobClick}
          previousAriaLabel={t('global.back')}
          nextAriaLabel={t('global.next')}
          emptyMessage={
            filterActive ? (
              <TableEmptyState
                title={t('settingsPanel.settingsPanel.jobPanel.noMatchingJobs')}
                hint={t('settingsPanel.settingsPanel.jobPanel.noMatchingJobsHint')}
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedJobStatuses([]);
                      setTypeFilterText('');
                    }}
                  >
                    {t('billingPanel.resetFilter')}
                  </Button>
                }
              />
            ) : (
              <TableEmptyState
                title={t('settingsPanel.settingsPanel.jobPanel.noJobs')}
                hint={t('settingsPanel.settingsPanel.jobPanel.noJobsHint')}
              />
            )
          }
        />
      </div>
      <Dialog
        open={jobDetailDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setJobDetailDialogOpen(false);
            setSelectedJob(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[1000px]">
          <DialogHeader>
            <DialogTitle className="min-w-0 break-words">
              {selectedJob?.type || t('settingsPanel.settingsPanel.jobPanel.jobDetails')}
            </DialogTitle>
          </DialogHeader>
          <JobDetailView
            job={selectedJob}
            onDismiss={() => {
              setJobDetailDialogOpen(false);
              setSelectedJob(null);
            }}
          />
          <DialogFooter>
            <Button
              className="max-sm:w-full"
              onClick={() => {
                setJobDetailDialogOpen(false);
                setSelectedJob(null);
              }}
            >
              {t('global.ok')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
